import Input from "components/atom/Input.tsx";
import FormCheckbox from "components/form/FormCheckbox.tsx";
import FormInputPhone from "components/form/FormInputPhone.tsx";
import { Fragment } from "react";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { UserFormValuesType } from "utils/form.ts";
import Select from "components/atom/Select.tsx";
import { CA_PROVINCES, USER_TYPES, LANGS } from "globals/constants.ts";
import { useLocation, useNavigate } from "react-router-dom";
import { tryAsyncFunc } from "globals/functions/async.ts";
import ajax from "config/ajax.ts";


function UserForm(props: { isAdmin?: boolean }) {
  const { isAdmin = false } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { values, setFieldValue, errors, resetForm } = useFormikContext<UserFormValuesType>();


  const verifyUnverifiedEmail = async () => {
    if (!values.unverifiedEmail) return;

    //dont await because we dont want to block the redirect
    tryAsyncFunc(ajax.post("/connected/auth/resend-code"));

    navigate({
      pathname: location.pathname,
      hash: "#/auth/verify-email"
    }, { state: { email: values.unverifiedEmail, redirect_to: location.pathname, coming_from: "user" } });
  };

  const cancelUnverifiedEmail = () => {
    if (!values.unverifiedEmail) return;

    tryAsyncFunc(ajax.delete("/connected/auth/unverified-email"));

    setFieldValue("unverifiedEmail", null, false);
    resetForm({
      values: {
        ...values,
        unverifiedEmail: null
      }
    });
  };

  return (
    <Fragment>
      <Input name="name" type="text"
             label={values.type === USER_TYPES.EVENT_MANAGER ? t("forms.businessName") : t("forms.name")} />

      <Input name="email" type="email" label={t("forms.email")} />

      {values.unverifiedEmail && (
        <div className="flex flex-row justify-between items-center gap-1">
          <button className="underline text-black text-xs" type="button" onClick={verifyUnverifiedEmail}>
            {t("forms.verifyEmail", { email: values.unverifiedEmail })}
          </button>
          <button className="underline text-red-500 text-xs" type="button" onClick={cancelUnverifiedEmail}>
            {t("forms.cancelUnverifiedEmail")}
          </button>
        </div>
      )}
      {values.type === USER_TYPES.EVENT_MANAGER && (
        <Fragment>
          <div className="my-2 rounded-md border border-gray-300 p-2">
            <p className="text-xs font-medium text-gray-300">
              {t("forms.address")}
            </p>
            <div className="grid grid-cols-1 gap-x-3 md:grid-cols-2">
              <Input name="address" type="text" label={t("forms.streetAddress")} />
              <Input name="city" type="text" label={t("forms.city")} />
            </div>
            <div className="grid grid-cols-1 gap-x-3 md:grid-cols-3">
              <Input name="zipcode" type="text" label={t("forms.zipcode")} />
              <Select name="state" type="text" label={t("forms.state")}>
                {Object.keys(CA_PROVINCES).map((stateCode) => (
                  <option key={stateCode} value={stateCode}>
                    {/* @ts-ignore */}
                    {CA_PROVINCES[stateCode]}
                  </option>
                ))}
              </Select>
              <Select disabled name="country" type="text" label={t("forms.country")}>
                <option value="CA">Canada</option>
              </Select>
            </div>
          </div>

          <FormCheckbox
            name="sameAddress"
            checkboxClassName="w-4 h-4 min-w-4"
            label={t("forms.sameAddress")}
            onChange={(checked) => setFieldValue("sameAddress", checked)}
            error={errors.sameAddress}
            checked={values.sameAddress} />

          {!values.sameAddress && (
            <div className="my-2 rounded-md border border-gray-300 p-2">
              <p className="text-xs font-medium text-gray-300">
                {t("forms.billingAddress")}
              </p>
              <div className="grid grid-cols-1 gap-x-3 md:grid-cols-2">
                <Input name="billingAddress" type="text" label={t("forms.streetAddress")} />
                <Input name="billingCity" type="text" label={t("forms.city")} />
              </div>
              <div className="grid grid-cols-1 gap-x-3 md:grid-cols-3">
                <Input name="billingZipcode" type="text" label={t("forms.zipcode")} />

                <Select name="billingState" type="text" label={t("forms.state")}>
                  {Object.keys(CA_PROVINCES).map((stateCode) => (
                    <option key={stateCode} value={stateCode}>
                      {/* @ts-ignore */}
                      {CA_PROVINCES[stateCode]}
                    </option>
                  ))}
                </Select>
                <Select disabled name="billingCountry" type="text" label={t("forms.country")}>
                  <option value="CA">Canada</option>
                </Select>
              </div>

            </div>
          )}

          <Input name="website" placeholder={t("forms.placeholders.url")} type="text" label={t("forms.website")} />


        </Fragment>
      )}
      <FormInputPhone
        label={t("forms.phoneNumber")}
        value={values.phoneNumber || ""}
        onChange={({ clean }) => setFieldValue("phoneNumber", clean)}
        name="phoneNumber"
        error={errors.phoneNumber}
        type="text" />
      <Select name="preferredLang" label={t("forms.preferredLang")}>
        <option value={LANGS.FR}>Français</option>
        <option value={LANGS.EN}>English</option>
      </Select>
    </Fragment>
  );
}

export default UserForm;
